/**
 * BinimelizPDFLayout — Layout PDF unificado al estilo de las cotizaciones
 * que Binimeliz envía a clientes (referencia: cotización 8128-26 a Rocmin).
 *
 * Reemplaza PurchaseOrderPDF y QuotationForm.printQuotation. Usa CSS print
 * para que al imprimir / guardar PDF se vea idéntico al modelo histórico.
 *
 * Props:
 *   tipo            "cotizacion" | "solicitud_cotizacion" | "orden_compra"
 *   numero          string  ej. "8128-26", "OCC-0007"
 *   fecha           Date
 *   recipiente      { rolLabel, razon, rut, direccion,
 *                     contactoNombre, contactoEmail, contactoTel }
 *   items           [{ cant, detalle, valorUnitario, valorTotal? (opt) }]
 *   nota            string libre (aparece en sección NOTA arriba de FACTURACIÓN)
 *   descuentoPct    number opcional (default 0)
 *   ivaPct          number (default 19)
 *   observaciones   { plazoEntrega, lugarEntrega, condicionesPago, validez }
 *   footer          { mensaje, firmanteNombre, firmanteCargo }
 *   onClose         fn — cierra el overlay
 */
import React, { useEffect } from "react";
import logoBinimeliz from "../../logo.jpg";
import "./BinimelizPDFLayout.css";

const BINIMELIZ_INFO = {
  nombre:      "BINIMELIZ S.A.",
  giroLabel:   "MECANICA DE PRECISION",
  rut:         "96.911.420-8",
  giroDetalle: "Fabricación de partes y piezas con maquinaria CNC - Compra y venta de maquinaria industrial",
  direccion:   "Caupolican 1203, Renca, RM",
  telefono:    "9 6334 3580 / 9 9746 7101",
};

const TIPO_TITULO = {
  cotizacion:           "COTIZACIÓN",
  solicitud_cotizacion: "SOLICITUD DE COTIZACIÓN",
  orden_compra:         "ORDEN DE COMPRA",
};

const TIPO_INTRO = {
  cotizacion:           "ESTIMADO(S) SEÑOR(ES) DE ACUERDO A LO SOLICITADO, TENEMOS EL AGRADO DE COTIZAR LO SIGUIENTE:",
  solicitud_cotizacion: "ESTIMADO(S) SEÑOR(ES), SOLICITAMOS COTIZAR LOS SIGUIENTES ÍTEMS:",
  orden_compra:         "ESTIMADO(S) SEÑOR(ES), MEDIANTE LA PRESENTE CONFIRMAMOS LA SIGUIENTE ORDEN DE COMPRA:",
};

const TIPO_FOOTER_DEFAULT = {
  cotizacion:           "Esperamos vernos favorecidos con su orden de compra, la cual tendrá nuestra mejor atención.",
  solicitud_cotizacion: "Agradecemos enviar su cotización a la mayor brevedad. Quedamos atentos a su respuesta.",
  orden_compra:         "Confirmamos la orden de compra. Quedamos a la espera del despacho según condiciones acordadas.",
};

const fmtCLP = n => {
  const v = Math.round(Number(n) || 0);
  return "$ " + v.toLocaleString("es-CL");
};
const fmtDate = d => {
  if (!d) return "";
  const date = d instanceof Date ? d : new Date(d);
  if (isNaN(date.getTime())) return "";
  return date.toLocaleDateString("es-CL", { day: "2-digit", month: "2-digit", year: "numeric" });
};

const BinimelizPDFLayout = ({
  tipo = "cotizacion",
  numero = "—",
  fecha = new Date(),
  recipiente = {},
  items = [],
  nota = "",
  descuentoPct = 0,
  descuentoMonto = null, // monto fijo en $ — tiene prioridad sobre descuentoPct
  ivaPct = 19,
  observaciones = {},
  footer = {},
  onClose,
}) => {
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  // Cálculos
  const itemsCalc = items.map((it, i) => {
    const cant = Number(it.cant) || 0;
    const vu   = Number(it.valorUnitario) || 0;
    const vt   = it.valorTotal != null ? Number(it.valorTotal) : cant * vu;
    return { idx: i + 1, cant, detalle: it.detalle || "", vu, vt };
  });
  const subtotal = itemsCalc.reduce((s, it) => s + it.vt, 0);
  const descuento = descuentoMonto != null
    ? Math.min(Math.round(Number(descuentoMonto) || 0), subtotal)
    : Math.round(subtotal * (Number(descuentoPct) || 0) / 100);
  const totalNeto = subtotal - descuento;
  const iva       = Math.round(totalNeto * (Number(ivaPct) || 0) / 100);
  const total     = totalNeto + iva;

  const titulo = TIPO_TITULO[tipo] || tipo.toUpperCase();
  const intro  = TIPO_INTRO[tipo]  || "";
  const footerMsg = footer.mensaje || TIPO_FOOTER_DEFAULT[tipo] || "";

  // Etiqueta para el bloque "INFORMACIÓN ___" según tipo
  const recipienteLabel = recipiente.rolLabel
    || (tipo === "cotizacion" ? "INFORMACION CLIENTE." : "INFORMACION PROVEEDOR.");

  const handlePrint = () => window.print();

  return (
    <div className="bzpdf-overlay">
      <div className="bzpdf-toolbar bzpdf-no-print">
        <button className="bzpdf-btn-primary" onClick={handlePrint}>
          🖨 Imprimir / Guardar PDF
        </button>
        {onClose && (
          <button className="bzpdf-btn-secondary" onClick={onClose}>Cerrar</button>
        )}
      </div>

      <div className="bzpdf-page">
        {/* Watermark — predominio del logo (estilo original Binimeliz),
            con sello dinámico discreto cada algunas celdas para conservar
            la propiedad anti-fraude (si alguien edita el monto sin
            actualizar el sello, queda inconsistencia visible). */}
        <div className="bzpdf-watermark" aria-hidden>
          {(() => {
            const stamp = `${titulo} N°${numero} · TOTAL ${fmtCLP(total)}`;
            // 7 columnas × 8 filas = 56 celdas. 1 de cada 6 muestra el sello,
            // el resto es logo grande. Domina el logo visualmente.
            const cells = [];
            const total_cells = 7 * 8;
            for (let i = 0; i < total_cells; i++) {
              const isStamp = i % 6 === 5; // 1 cada 6
              cells.push(
                isStamp
                  ? <span key={i} className="bzpdf-wm-stamp">{stamp}</span>
                  : (logoBinimeliz
                    ? <img key={i} src={logoBinimeliz} alt="" className="bzpdf-wm-logo" />
                    : <span key={i} className="bzpdf-wm-stamp">Binimeliz</span>)
              );
            }
            return cells;
          })()}
        </div>

        <div className="bzpdf-content">
          {/* ── HEADER ─────────────────────────────────────────────── */}
          <div className="bzpdf-header">
            <div className="bzpdf-header-logo">
              {logoBinimeliz && <img src={logoBinimeliz} alt="Binimeliz" />}
            </div>
            <div className="bzpdf-header-info">
              <div className="bzpdf-empresa">
                <span className="bzpdf-empresa-nombre">{BINIMELIZ_INFO.nombre}</span>
                <span className="bzpdf-empresa-giro"> {BINIMELIZ_INFO.giroLabel}</span>
              </div>
              <div className="bzpdf-empresa-data">RUT: {BINIMELIZ_INFO.rut}</div>
              <div className="bzpdf-empresa-data">Giro: {BINIMELIZ_INFO.giroDetalle}</div>
              <div className="bzpdf-empresa-data">Dirección: {BINIMELIZ_INFO.direccion}</div>
              <div className="bzpdf-empresa-data">Teléfonos: {BINIMELIZ_INFO.telefono}</div>
            </div>
            <div className="bzpdf-header-titulo">
              <div className="bzpdf-titulo-text">{titulo}</div>
              <div className="bzpdf-titulo-num">N° {numero}</div>
            </div>
          </div>

          {/* ── INFORMACIÓN CLIENTE/PROVEEDOR ─────────────────────── */}
          <div className="bzpdf-section-header">{recipienteLabel}</div>
          <div className="bzpdf-recipient">
            <table>
              <tbody>
                <tr>
                  <td className="bzpdf-recipient-label">Señor(es):</td>
                  <td className="bzpdf-recipient-value">{recipiente.razon || "—"}</td>
                  <td className="bzpdf-recipient-label">Solicitante:</td>
                  <td className="bzpdf-recipient-value">{recipiente.contactoNombre || "—"}</td>
                </tr>
                <tr>
                  <td className="bzpdf-recipient-label">RUT:</td>
                  <td className="bzpdf-recipient-value">{recipiente.rut || "—"}</td>
                  <td className="bzpdf-recipient-label">Email:</td>
                  <td className="bzpdf-recipient-value bzpdf-link">{recipiente.contactoEmail || "—"}</td>
                </tr>
                <tr>
                  <td className="bzpdf-recipient-label">Dirección:</td>
                  <td className="bzpdf-recipient-value">{recipiente.direccion || "—"}</td>
                  <td className="bzpdf-recipient-label">Teléfono:</td>
                  <td className="bzpdf-recipient-value">{recipiente.contactoTel || "—"}</td>
                </tr>
                <tr>
                  <td className="bzpdf-recipient-label">Fecha:</td>
                  <td className="bzpdf-recipient-value" colSpan={3}>{fmtDate(fecha)}</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* ── INTRO ─────────────────────────────────────────────── */}
          <div className="bzpdf-intro">{intro}</div>

          {/* ── ITEMS ─────────────────────────────────────────────── */}
          <table className="bzpdf-items">
            <thead>
              <tr>
                <th className="bzpdf-th-num">Item</th>
                <th className="bzpdf-th-cant">Cant.</th>
                <th className="bzpdf-th-det">Detalle</th>
                <th className="bzpdf-th-vu">Valor Unitario</th>
                <th className="bzpdf-th-vt">Valor Total</th>
              </tr>
            </thead>
            <tbody>
              {itemsCalc.map(it => (
                <tr key={it.idx}>
                  <td className="bzpdf-td-num">{it.idx}</td>
                  <td className="bzpdf-td-cant">{it.cant}</td>
                  <td className="bzpdf-td-det">{it.detalle}</td>
                  <td className="bzpdf-td-vu">
                    {tipo === "solicitud_cotizacion" && it.vu === 0
                      ? <span className="bzpdf-td-pendiente">— por cotizar —</span>
                      : fmtCLP(it.vu)}
                  </td>
                  <td className="bzpdf-td-vt">
                    {tipo === "solicitud_cotizacion" && it.vt === 0
                      ? "—"
                      : fmtCLP(it.vt)}
                  </td>
                </tr>
              ))}
              {/* relleno visual hasta tener 12 filas para mantener altura del PDF estable */}
              {Array.from({ length: Math.max(0, 12 - itemsCalc.length) }).map((_, i) => (
                <tr key={"fill-" + i} className="bzpdf-tr-fill">
                  <td colSpan={5}>&nbsp;</td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* ── NOTA + FACTURACIÓN ────────────────────────────────── */}
          <div className="bzpdf-totales-row">
            <div className="bzpdf-nota">
              <div className="bzpdf-nota-header">NOTA:</div>
              <div className="bzpdf-nota-body">{nota || ""}</div>
            </div>
            <div className="bzpdf-facturacion">
              <div className="bzpdf-fact-header">Facturacion.</div>
              <table>
                <tbody>
                  <tr>
                    <td className="bzpdf-fact-label">SubTotal</td>
                    <td className="bzpdf-fact-value">{fmtCLP(subtotal)}</td>
                  </tr>
                  <tr>
                    <td className="bzpdf-fact-label">Descuento</td>
                    <td className="bzpdf-fact-value">
                      {descuento > 0
                        ? (descuentoMonto == null && descuentoPct > 0
                            ? `${descuentoPct}% (${fmtCLP(descuento)})`
                            : fmtCLP(descuento))
                        : "—"}
                    </td>
                  </tr>
                  <tr>
                    <td className="bzpdf-fact-label">Total Neto</td>
                    <td className="bzpdf-fact-value">{fmtCLP(totalNeto)}</td>
                  </tr>
                  <tr>
                    <td className="bzpdf-fact-label">I.V.A. ({ivaPct}%)</td>
                    <td className="bzpdf-fact-value">{fmtCLP(iva)}</td>
                  </tr>
                  <tr className="bzpdf-fact-total">
                    <td className="bzpdf-fact-label">Total</td>
                    <td className="bzpdf-fact-value">{fmtCLP(total)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* ── OBSERVACIONES ─────────────────────────────────────── */}
          <div className="bzpdf-section-header">Observaciones.</div>
          <div className="bzpdf-observaciones">
            <div>
              <span className="bzpdf-obs-label">Plazo de entrega:</span>{" "}
              <span className="bzpdf-obs-value">{observaciones.plazoEntrega || "—"}</span>
            </div>
            <div>
              <span className="bzpdf-obs-label">Lugar de entrega:</span>{" "}
              <span className="bzpdf-obs-value">{observaciones.lugarEntrega || "—"}</span>
            </div>
            <div>
              <span className="bzpdf-obs-label">Condiciones de pago:</span>{" "}
              <span className="bzpdf-obs-value">{observaciones.condicionesPago || "—"}</span>
            </div>
            <div>
              <span className="bzpdf-obs-label">Validez de la oferta:</span>{" "}
              <span className="bzpdf-obs-value">{observaciones.validez || "—"}</span>
            </div>
          </div>

          {/* ── FOOTER ────────────────────────────────────────────── */}
          <div className="bzpdf-footer">
            <div className="bzpdf-footer-msg">{footerMsg}</div>
            <div className="bzpdf-firma">
              <div className="bzpdf-firma-linea" />
              <div className="bzpdf-firma-nombre">{footer.firmanteNombre || ""}</div>
              <div className="bzpdf-firma-cargo">{footer.firmanteCargo || "p.p. BINIMELIZ S.A."}</div>
            </div>
            {/* Verificación: línea con datos clave embebidos. Si el monto u otro
                campo cambia en el PDF sin modificar también esta línea, queda
                evidencia. Junto con el watermark dinámico es doble check. */}
            <div className="bzpdf-verify">
              Documento {titulo.toLowerCase()} N°{numero} · {recipiente.razon || "—"} · {fmtDate(fecha)} · neto {fmtCLP(totalNeto)} · IVA {fmtCLP(iva)} · <strong>TOTAL {fmtCLP(total)}</strong>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BinimelizPDFLayout;
